import { lazy } from 'react';
import { CalendarModuleConfig } from './types';

const CalendarWhatsNew = lazy(() => import('./CalendarWhatsNew'));

/**
 * Calendars Module Config
 *
 * SINGLE SOURCE OF TRUTH:
 * Permissions are defined ONCE in the backend (calendar/permissions.ts)
 * and fetched via the modulePermissions GraphQL query.
 *
 * Routes and nav items use `permissionLevel` (e.g. "view", "create", "edit", "admin")
 * which gets resolved to actual permission strings at runtime from the backend.
 *
 * To change permissions: Edit business-builder-backend/src/modules/calendar/permissions.ts
 * That's the only place. The frontend picks it up automatically.
 */
export const calendarsModuleConfig: CalendarModuleConfig = {
  id: 'calendars',
  name: 'Calendars',
  icon: '📅',
  color: 'purple',
  description: 'Manage business calendars and events with public booking system',
  version: '1.9.0',
  enabled: true,
  category: 'productivity',
  order: 15,

  // ROUTES - permissionLevel is resolved from backend at runtime
  routes: [
    {
      // One click to the calendar the person lives in (2026-09-25). Redirects; renders nothing of its own.
      // Two addresses, one page: the short `/calendar` is the one to type or bookmark. See CLAUDE.md
      // § "Your main calendar" for how the route is set up and ported.
      path: '/calendars/main',
      component: lazy(() => import('./OpenMainCalendar')),
      exact: true,
      permissionLevel: 'view',
      useLayout: true,
      layoutOptions: { maxW: "container.lg" },
    },
    {
      path: '/calendar',
      component: lazy(() => import('./OpenMainCalendar')),
      exact: true,
      permissionLevel: 'view',
      useLayout: true,
      layoutOptions: { maxW: "container.lg" },
    },
    {
      path: '/calendars/birds-eye',
      component: lazy(() => import('./BirdsEyeView')),
      exact: true,
      permissionLevel: 'view',
      useLayout: true,
    },
    {
      path: '/calendars/my',
      component: lazy(() => import('./MyCalendars')),
      exact: true,
      permissionLevel: 'view',
      useLayout: true,
      layoutOptions: { maxW: "container.lg" },
    },
    {
      path: '/calendars/new',
      component: lazy(() => import('./NewCalendar')),
      permissionLevel: 'create',
      useLayout: true,
      layoutOptions: { maxW: "container.lg" },
    },
    {
      path: '/calendars/:id/edit',
      component: lazy(() => import('./EditCalendar')),
      permissionLevel: 'edit',
      useLayout: true,
      layoutOptions: { maxW: "container.lg" },
    },
    {
      path: '/calendars/view',
      component: lazy(() => import('./CalendarView')),
      permissionLevel: 'view',
      useLayout: true,
      layoutOptions: { noContainer: true },
    },
    {
      path: '/calendars/:id/view',
      component: lazy(() => import('./CalendarView')),
      permissionLevel: 'view',
      useLayout: true,
      layoutOptions: { noContainer: true },
    },
    {
      path: '/calendars/:id/event-types',
      component: lazy(() => import('./EventTypesManagement')),
      permissionLevel: 'edit',
      useLayout: true,
    },
    {
      path: '/calendars/:id/availability',
      component: lazy(() => import('./AvailabilitySettings')),
      permissionLevel: 'edit',
      useLayout: true,
    },
    {
      path: '/calendars/:id',
      component: lazy(() => import('./CalendarDetails')),
      permissionLevel: 'view',
      useLayout: true,
      layoutOptions: { maxW: "container.md" },
    },
    {
      path: '/calendar/admin/calendars',
      component: lazy(() => import('./CalendarAccountsAdmin')),
      permissionLevel: 'admin',
      useLayout: true,
      layoutOptions: { noContainer: true },
    },
    {
      path: '/calendars/life-goals',
      component: lazy(() => import('./life-goals/LifeGoalsTimeline')),
      permissionLevel: 'view',
      useLayout: true,
      layoutOptions: { noContainer: true },
    },
    {
      path: '/calendars/whats-new',
      component: CalendarWhatsNew,
      permissionLevel: 'admin',
      useLayout: true,
    }
  ],

  // NAVIGATION - permissionLevel is resolved from backend at runtime
  navigation: [
    {
      label: 'My main calendar',
      path: '/calendars/main',
      icon: '⭐',
      permissionLevel: 'view',
    },
    {
      label: "Bird's Eye View",
      path: '/calendars/birds-eye',
      icon: '🦅',
      permissionLevel: 'view',
    },
    {
      label: 'My Calendars',
      path: '/calendars/my',
      icon: '📋',
      permissionLevel: 'view',
    },
    {
      label: 'Life Goals Timeline',
      path: '/calendars/life-goals',
      icon: '🎯',
      permissionLevel: 'view',
    },
    {
      label: 'New Calendar',
      path: '/calendars/new',
      icon: '➕',
      permissionLevel: 'create',
    },
    {
      label: 'Calendar Admin',
      path: '/calendar/admin/calendars',
      icon: '⚙️',
      permissionLevel: 'admin',
    },
    {
      label: "What's New",
      path: '/calendars/whats-new',
      icon: '🎉',
      permissionLevel: 'admin',
    }
  ],

  // Permissions come from the backend (single source of truth).
  // See: business-builder-backend/src/modules/calendar/permissions.ts
  // These empty arrays get replaced at runtime by ModulePermissionsContext.
  permissions: {
    view: [],
    create: [],
    edit: [],
    delete: [],
    admin: [],
    share: [],
  },
  quickActions: [
    {
      label: 'My main calendar',
      path: '/calendars/main',
      icon: '⭐',
      description: 'Open the calendar you live in, in one click'
    },
    {
      label: 'My Calendars',
      path: '/calendars/my',
      icon: '📋',
      description: 'View all your calendars'
    },
    {
      label: 'New Calendar',
      path: '/calendars/new',
      icon: '➕',
      description: 'Create a new calendar'
    }
  ],

  // 📋 CHANGELOG
  changelog: [
    {
      version: "1.9.0",
      releaseDate: "2026-09-25",
      title: "Your main calendar, one click away — and the whole module easier to read",
      features: [
        "✅ Pick your main calendar once (My Calendars asks) — “My main calendar” in the calendar menu then opens it in one click, on every device",
        "✅ My Calendars redesigned: your main calendar at the top with what's coming up, every other calendar in a calm list with Open and More",
        "✅ Event details read clearly in every view: 14px text, no clutter icons, dark text on pale event colours, start times in Month view",
        "✅ Phones show three days side by side instead of a squeezed week, with Day · 3 days · List buttons",
        "✅ Light and dark mode both look right — the day headers no longer glare on the dark site",
        "✅ Every page follows the POLISH v1 standard: words on every button, plain words instead of codes, clear loading and error messages",
      ],
    },
    {
      version: "1.8.0",
      releaseDate: "2026-07-28",
      title: "Editing a meeting now offers to tell the people in it",
      features: [
        "✅ Change the time, date, title, location, description or join link of an event that already sent invites, and the calendar asks: send an updated invite to attendees? Sending is the default; declining is one click.",
        "✅ Dragging or resizing an event on the grid asks the same question before it emails anyone — declining puts the event back where it was",
        "✅ An update REPLACES the meeting in their Google/Outlook/Apple calendar instead of adding a second copy — the invite carries the same identifier with a higher version, as the calendar standard requires",
        "✅ Colour, tags, visibility and reminders never trigger the prompt — only things an attendee reads off their own calendar",
        "✅ You are told what actually happened: 'Updated invite sent to 2 attendees', or the reason nobody was told",
        "✅ Removing someone from a meeting now cancels it in their calendar; deleting the meeting reports how many were told",
        "✅ People who were only ever listed as guests are never emailed about a meeting they were never sent",
        "✅ All-day events show the right day everywhere, including for anyone viewing from outside Australia",
        "✅ The Agenda view now says 'All Day' instead of inventing a time range",
        "✅ Filter panel counts describe the whole calendar, not just what survived the filters you already have on",
        "✅ The events badge says '3 of 40' when filters are hiding things, and the email-listening banner no longer claims 'not listening' while it is still checking",
      ],
    },
    {
      version: "1.7.0",
      releaseDate: "2026-07-23",
      title: "Clearer invites + staff-aware attendees",
      features: [
        "✅ Event kinds as grouped picker cards — a Video meeting is plainly 'an iCal invite with a video join link'",
        "✅ Invite Email Source: send invites to staff WORK or personal email; every attendee chip shows the exact address",
        "✅ Attendee search is server-side and staff-aware — matches nicknames, work AND personal emails; Staff/Contact badges",
        "✅ Broadcast Delivery block: send-mode + address choice in one clear panel",
        "✅ Richer read-only event view (join button, RSVP statuses, larger type)",
      ],
    },
    {
      version: "1.6.0",
      releaseDate: "2026-07-16",
      title: "Calendar Sharing, Pinning & the Baby-Boomer Pass",
      features: [
        "✅ Share any calendar with specific people — type a name (nicknames work, shown in brackets), pick 'Can view' or 'Can view & edit', done",
        "✅ Share with a whole GROUP — everyone holding an employee role, or every client with a tag; the share keeps itself up to date as people join or leave",
        "✅ Always see exactly who a group share reaches before you confirm — a live preview lists every person by name",
        "✅ Search results show EMPLOYEE (with their roles) or CLIENT badges; people who no longer work here never appear",
        "✅ Everyone you share with gets an in-app notification (and again when access changes or is removed)",
        "✅ 'Shared With Me' now shows a badge telling you whether you can edit or just view each calendar",
        "✅ Pin your favourite calendars — a 📌 Pinned section keeps them at the top of My Calendars",
        "✅ Life Goals timeline: 'Show Calendar Events' now actually shows your regular events alongside life goals",
        "✅ Calendar Management admin: sortable columns, filters that remember themselves, pagination, and a Share action",
        "✅ Fixed: saving a calendar from the admin Edit window no longer fails with a 400 error",
        "✅ Plain-English hover tips on every button across the module — hover anything to learn what it does"
      ],
      notes: "Sharing is person-to-person with per-person permissions, enforced server-side (view-only users can't change events). Email-linked calendar access is unchanged and works alongside direct shares."
    },
    {
      version: "1.5.0",
      releaseDate: "2026-07-13",
      title: "Goals 2.0 — Inline Editing, Gamification & Life-Goal Links",
      features: [
        "✅ Goal cards are now the editor: add, tick, reorder (↑/↓) and remove tasks inline — no modal round-trips",
        "✅ +/- stepper on numeric Current values with 'what are we counting' unit labels (kpiMetric)",
        "✅ Goal images (IPFS, chunked upload) with emoji + category-gradient fallback banners",
        "✅ Progress rings, count progress bars with milestone ticks, 🏆 champion state at 100%",
        "✅ Pace bar: task completion racing the month's elapsed days (ahead/behind at a glance)",
        "✅ 🔥 per-goal streaks, ✅ ticks-today chip, 😴 neglect sort (least-touched goals float first)",
        "✅ 👉 NEXT pointer on each goal's first open task",
        "✅ Client scoreboard: avatar chips with average progress, click to filter",
        "✅ Monthly goals can link to Life Goals — rollups shown on the Life Goals timeline",
        "✅ Weekly recap email every Sunday 6pm Brisbane (pace, per-goal lines, neglected-goal callout)",
        "✅ Goals toggle persists; Fit view hides the breadcrumb bar; Expand starts at page top",
        "✅ Toolbar tooltips on every control; quick-add goal row; category filter chips (tabs removed)"
      ],
      notes: "Major goals overhaul: the monthly goals panel becomes a daily driver — inline task management with optimistic updates and client-minted ids (no add-then-tick race), gamified progress (streaks, pace bar, scoreboard), and a first bridge between monthly goals and the Life Goals timeline via linkedLifeGoalEventId."
    },
    {
      version: "1.3.0",
      releaseDate: "2025-11-24",
      title: "HTML Email Support with WYSIWYG Editor & Tenant Branding",
      features: [
        "✅ Integrated WYSIWYG HTML email editor for calendar email broadcasts",
        "✅ Rich text formatting: bold, italic, underline, lists, links, code blocks, text alignment",
        "✅ Hyperlinks in emails are now clickable (main issue resolved!)",
        "✅ Visual and source editing modes for HTML emails",
        "✅ HTML preview and plain text preview tabs",
        "✅ Tenant branding applied to all broadcast emails (logo, colors, site name)",
        "✅ Professional email templates with header and footer branding",
        "✅ Links styled with tenant primary color for brand consistency",
        "✅ Mobile-responsive email templates",
        "✅ Plain text fallback for email clients that don't support HTML"
      ],
      notes: "Major upgrade to email broadcast system. Users can now create rich, formatted emails with clickable links using a professional WYSIWYG editor. All broadcast emails automatically include tenant branding (logo and colors) for a polished, professional appearance. This resolves the critical issue where plain text links were not clickable in emails."
    },
    {
      version: "1.2.0",
      releaseDate: "2025-11-22",
      title: "SMS Sender Configuration & Event Deletion Improvements",
      features: [
        "✅ Enhanced SMS sender selection with visual radio button interface",
        "✅ Display both Alpha ID and Dedicated Number options with tenant-specific values",
        "✅ Fixed GraphQL query to properly fetch tenant SMS configuration (alphaId, dedicatedNumber)",
        "✅ Auto-set broadcast event end time to start time + 1 minute (SMS, Email, Both)",
        "✅ Smart time formatting for datetime-local inputs (YYYY-MM-DDTHH:MM)",
        "✅ Only auto-set end time when creating events, not when editing",
        "✅ Made Event ID copyable with click-to-copy button and selectable text",
        "✅ Fixed event deletion to permanently remove from database (not just mark as cancelled)",
        "✅ Added DELETE_EVENT mutation for proper database cleanup",
        "✅ Updated confirmation message to clarify deletion prevents broadcasts",
        "✅ Changed button text from 'Cancel Event' to 'Delete Event' for clarity",
        "✅ Removed debug console logging for cleaner production environment"
      ],
      notes: "Critical fixes for SMS broadcast configuration and event deletion. The new sender selection UI clearly shows both Alpha ID and Dedicated Number options with actual tenant values. Event deletion now properly removes records from the database, preventing scheduled broadcasts from being sent for deleted events. The auto-end-time feature for broadcast events improves UX by automatically setting appropriate durations for instant broadcast triggers."
    },
    {
      version: "1.1.0",
      releaseDate: "2024-09-08",
      title: "Enhanced Broadcast System with Intuitive Tag-Based Selection",
      features: [
        "✅ Tag-based client group selection for SMS/Email broadcasts",
        "✅ Always-visible tag browser (no button clicking required)",
        "✅ Click tags to instantly select all clients in that group",
        "✅ Remove individual recipients with X button after group selection",
        "✅ 'Clear All' button to deselect all recipients at once",
        "✅ Real-time recipient preview showing exactly who will receive the broadcast",
        "✅ Hover tooltips on tags preview client names before selection",
        "✅ Double-click tags to expand and view full client details",
        "✅ Improved event form flow: Date/Time moved above Event Type for better UX",
        "✅ Step-by-step broadcast configuration (Step 1: SMS, Step 2: Email, Step 3: Recipients)",
        "✅ Fixed critical bug: Emails now send correctly in BOTH_BROADCAST mode",
        "✅ Separate tracking for SMS and Email broadcasts to prevent cross-channel conflicts",
        "✅ Client tags displayed in recipient preview with phone numbers",
        "✅ Comprehensive broadcast testing checklist added"
      ],
      notes: "Major UX overhaul of the broadcast system with intuitive tag-based selection and step-by-step configuration flow. Fixed a critical bug where emails were incorrectly skipped when sending both SMS and email broadcasts. The new linear workflow guides users through message creation and recipient selection with clear visual steps."
    },
    {
      version: "1.0.0",
      releaseDate: "2023-04-15",
      title: "Production Release - Complete Calendar & Booking System",
      features: [
        "✅ Multi-calendar support with color-coded views (Month/Week/Day/Agenda)",
        "✅ Mobile-responsive design with touch-optimized controls",
        "✅ Click-to-create events in all calendar views (including mobile tap support)",
        "✅ Drag & drop event scheduling with time adjustment",
        "✅ Public booking system (Calendly-style) with shareable links",
        "✅ Event type management with custom durations and pricing",
        "✅ Custom booking questions (short text, long text, dropdown, radio, checkboxes)",
        "✅ Availability scheduling with timezone support (14 timezones)",
        "✅ Blocked dates and time-off management",
        "✅ iCal integration (import/export, email invites)",
        "✅ Email-to-calendar event creation",
        "✅ SMS broadcast scheduling",
        "✅ Event notes and task checklists",
        "✅ Monthly goals with checkpoint tracking",
        "✅ Life goals timeline with long-term planning",
        "✅ Calendar sharing with granular permissions",
        "✅ Multi-timezone display for international teams",
        "✅ Recurring events (RRULE format)",
        "✅ Event filtering by type, tags, and calendar",
        "✅ Scroll position persistence with localStorage",
        "✅ Calendar admin dashboard for system management"
      ],
      notes: "First production release includes complete calendar management system with professional booking capabilities, mobile optimization, and extensive integration options."
    }
  ],

  // 🗺️ ROADMAP
  roadmap: [
    {
      title: "Enhanced Mobile Experience",
      description: "Continue improving mobile responsiveness and touch interactions",
      targetVersion: "1.1.0",
      priority: "high",
      status: "in-progress",
      features: [
        "✅ Mobile-friendly event modal (full-screen on mobile)",
        "✅ Touch-optimized calendar controls",
        "✅ Responsive calendar toolbar with wrapping badges",
        "⏳ Swipe gestures for week/day navigation",
        "⏳ Mobile-optimized event type cards",
        "⏳ Touch-friendly time picker for bookings"
      ]
    },
    {
      title: "Advanced Booking Features",
      description: "Enhanced public booking system with payment integration and automation",
      targetVersion: "1.2.0",
      priority: "high",
      status: "planned",
      features: [
        "Payment processing for paid bookings (Stripe integration)",
        "Booking confirmation emails with calendar attachments",
        "SMS reminders for upcoming bookings",
        "Automated follow-up sequences",
        "Buffer time between bookings",
        "Team scheduling (round-robin, collective availability)",
        "Booking analytics and conversion tracking",
        "Custom branding for booking pages"
      ]
    },
    {
      title: "Calendar Integrations",
      description: "Two-way sync with external calendar systems",
      targetVersion: "1.3.0",
      priority: "medium",
      status: "planned",
      features: [
        "Google Calendar bi-directional sync",
        "Microsoft Outlook/Office 365 sync",
        "Apple iCloud calendar sync",
        "Zoom meeting auto-creation",
        "Microsoft Teams meeting links",
        "Google Meet integration",
        "Webhook support for external systems"
      ]
    },
    {
      title: "Team Collaboration",
      description: "Enhanced features for team calendar management",
      targetVersion: "1.4.0",
      priority: "medium",
      status: "planned",
      features: [
        "Resource booking (meeting rooms, equipment)",
        "Team availability view (see everyone's calendar)",
        "Calendar overlays for comparison",
        "Shared event templates",
        "Team-wide blocked dates",
        "Delegation and proxy booking",
        "Department-specific calendars"
      ]
    },
    {
      title: "Analytics & Reporting",
      description: "Insights into calendar usage and booking performance",
      targetVersion: "1.5.0",
      priority: "low",
      status: "planned",
      features: [
        "Booking conversion rates",
        "Popular time slots analysis",
        "Event type performance metrics",
        "No-show tracking and reporting",
        "Revenue analytics for paid bookings",
        "Calendar utilization reports",
        "Custom booking reports and exports"
      ]
    },
    {
      title: "Automation & AI",
      description: "Intelligent scheduling and automated workflows",
      targetVersion: "2.0.0",
      priority: "low",
      status: "planned",
      features: [
        "AI-powered optimal time suggestions",
        "Smart meeting duration predictions",
        "Automated rescheduling for conflicts",
        "Intelligent booking question suggestions",
        "Natural language event creation",
        "Predictive availability forecasting",
        "Auto-categorization of event types"
      ]
    }
  ]
};